import React, { useState } from 'react'

import { Box, Button, Typography } from '@material-ui/core'
import { ChevronLeft, ChevronRight } from '@material-ui/icons'
import clsx from 'clsx'
import { isEmpty } from 'lodash'

import useStyles from './styles'
import courseConstants from 'constants/course'

const isMultipleCorrect = (question) => (question?.correctCount ?? 0) > 1

const isQuestionAnswered = (question, selections) => {
  const value = selections[question?.id]
  if (isMultipleCorrect(question)) {
    return Array.isArray(value) && value.length === question.correctCount
  }
  return value != null
}

const ExerciseQuiz = ({ exercise, onSubmit, isSubmitting }) => {
  const classes = useStyles()
  const { LETTERS } = courseConstants

  const questions = exercise?.questions || []
  const [currentIndex, setCurrentIndex] = useState(0)
  const [selections, setSelections] = useState({})

  const currentQuestion = questions[currentIndex]
  const isLastQuestion = currentIndex === questions.length - 1
  const isFirstQuestion = currentIndex === 0
  const isMultiple = isMultipleCorrect(currentQuestion)
  const currentValue =
    selections[currentQuestion?.id] ?? (isMultiple ? [] : null)

  const allAnswered = questions.every((question) =>
    isQuestionAnswered(question, selections),
  )

  const handleSelectAlternative = (alternativeId) => {
    if (isMultiple) {
      setSelections((previous) => {
        const current = Array.isArray(previous[currentQuestion.id])
          ? previous[currentQuestion.id]
          : []
        const isAlreadySelected = current.includes(alternativeId)
        return {
          ...previous,
          [currentQuestion.id]: isAlreadySelected
            ? current.filter((id) => id !== alternativeId)
            : [...current, alternativeId],
        }
      })
    } else {
      setSelections((previous) => ({
        ...previous,
        [currentQuestion.id]: alternativeId,
      }))
    }
  }

  const handleFinalize = () => {
    const answers = questions.map((question) => {
      const value = selections[question.id]
      const alternativeIds = isMultipleCorrect(question)
        ? Array.isArray(value)
          ? value
          : []
        : value != null
        ? [value]
        : []
      return { questionId: question.id, alternativeIds }
    })
    onSubmit(answers)
  }

  if (isEmpty(questions)) return null

  return (
    <Box>
      <Box className={classes.progressHeader}>
        <Typography className={classes.progressLabel}>
          Questão {currentIndex + 1} de {questions.length}
        </Typography>
        <Box className={classes.stepsRow}>
          {questions.map((question, index) => (
            <Box
              key={question?.id}
              className={clsx(classes.step, {
                [classes.stepActive]: index === currentIndex,
                [classes.stepAnswered]:
                  index !== currentIndex &&
                  isQuestionAnswered(question, selections),
              })}
            />
          ))}
        </Box>
      </Box>

      <Box className={classes.questionCard}>
        <Typography className={classes.questionStatement}>
          {currentIndex + 1}. {currentQuestion?.statement}
        </Typography>

        {isMultiple && (
          <Typography
            className={classes.multipleCorrectHint}
            color="textSecondary"
          >
            Selecione {currentQuestion.correctCount} alternativa
            {currentQuestion.correctCount !== 1 ? 's' : ''} correta
            {currentQuestion.correctCount !== 1 ? 's' : ''}
          </Typography>
        )}

        <Box className={classes.alternativesList}>
          {currentQuestion?.alternatives?.map((alternative, index) => {
            const alternativeId = alternative?.id
            const isSelected = isMultiple
              ? Array.isArray(currentValue) &&
                currentValue.includes(alternativeId)
              : currentValue === alternativeId

            return (
              <Box
                key={alternativeId}
                className={clsx(classes.alternativeRow, {
                  [classes.alternativeRowSelected]: isSelected,
                })}
                onClick={() => handleSelectAlternative(alternativeId)}
              >
                <Box
                  className={clsx(classes.alternativeLetter, {
                    [classes.alternativeLetterSelected]: isSelected,
                  })}
                >
                  {LETTERS[index]}
                </Box>
                <Typography
                  className={clsx(classes.alternativeContent, {
                    [classes.alternativeContentSelected]: isSelected,
                  })}
                >
                  {alternative?.content}
                </Typography>
              </Box>
            )
          })}
        </Box>

        <Box className={classes.navigation}>
          <Button
            variant="contained"
            size="small"
            startIcon={<ChevronLeft />}
            onClick={() => setCurrentIndex((previous) => previous - 1)}
            disabled={isFirstQuestion}
            className={classes.prevButton}
          >
            <span className={classes.buttonTextFull}>Pergunta anterior</span>
            <span className={classes.buttonTextShort}>Anterior</span>
          </Button>

          {isLastQuestion ? (
            <Button
              key="finalize"
              variant="contained"
              size="small"
              endIcon={<ChevronRight />}
              disabled={isSubmitting || !allAnswered}
              onClick={handleFinalize}
              className={classes.nextButton}
            >
              <span className={classes.buttonTextFull}>
                Finalizar questionário
              </span>
              <span className={classes.buttonTextShort}>Finalizar</span>
            </Button>
          ) : (
            <Button
              key="next"
              variant="contained"
              size="small"
              endIcon={<ChevronRight />}
              disabled={!isQuestionAnswered(currentQuestion, selections)}
              onClick={() => setCurrentIndex((previous) => previous + 1)}
              className={classes.nextButton}
            >
              <span className={classes.buttonTextFull}>Próxima pergunta</span>
              <span className={classes.buttonTextShort}>Próximo</span>
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default ExerciseQuiz
